import {AttrsSchema} from 'panel';

import {BUTTON_ROOT_CLASS, ButtonLayout, ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import type {TooltipAttrs} from 'common/components/tooltip/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {SimpleNavManager} from 'common/contexts/nav-manager/simple-nav-manager';

import {BaseButtonAttrs, ButtonBase} from '../base/button';
import {template} from './template';
import css from './panel.styl?inline';

export interface ButtonJsxProps {
  attrs?: ButtonAttrs;
  params?: ButtonParams;
  on?: {
    click?: (ev: MouseEvent) => void;
  };
}

type Attrs = {
  class?: string;
  'highlight-icon'?: boolean;
  'fill-icon'?: boolean;
  icon?: string;
  'icon-color'?: string;
  'icon-right'?: string;
  size?: ButtonSize;
  theme?: ButtonTheme;
  type?: ButtonType;
  pressed?: boolean;
  tooltip?: TooltipAttrs;
  layout?: ButtonLayout;
  'disable-pressed-styles'?: boolean;
  'disabled-stays-opaque'?: boolean;
  href?: string;
  download?: string;
  target?: string;
};

type ButtonParams = {
  label?: string;
  tooltip?: TooltipAttrs;
};

export type ButtonAttrs = BaseButtonAttrs & Attrs;

export {BUTTON_ROOT_CLASS};
export {ButtonReact} from './component';
export type {ButtonProps} from './types';

/**
 * Attributes mirrored onto the `<mp-button>` host as `data-button-*`, next to `BUTTON_ROOT_CLASS`, so
 * consumer CSS and tests can target the button through the same selectors as the React implementation.
 */
const HOST_DATA_ATTR_SOURCES = [`theme`, `size`, `layout`, `icon`, `pressed`, `disabled`, `readonly`] as const;

export class Button extends ButtonBase<Attrs, ButtonParams, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<ButtonAttrs> {
    return {
      ...super.attrsSchema,
      class: `string`,
      'highlight-icon': `boolean`,
      'fill-icon': `boolean`,
      icon: `string`,
      'icon-color': `string`,
      'icon-right': `string`,
      size: {type: `string`, enum: Object.values(ButtonSize), default: ButtonSize.Medium},
      theme: {type: `string`, enum: Object.values(ButtonTheme), default: ButtonTheme.Secondary},
      type: {type: `string`, enum: Object.values(ButtonType), default: ButtonType.Info},
      layout: {type: `string`, enum: Object.values(ButtonLayout), default: ButtonLayout.Horizontal},
      pressed: `boolean`,
      tooltip: `json`,
      'disable-pressed-styles': `boolean`,
      'disabled-stays-opaque': `boolean`,
      href: `string`,
      target: `string`,
      download: `string`,
    };
  }

  get config() {
    return Object.assign(super.config, {
      css,
      template,
      contexts: [`navManager` as const],
      defaultContexts: {
        navManager: new SimpleNavManager(),
      },
      params: {
        label: String,
        tooltip: Object,
      },
      hooks: {
        preUpdate: () => {
          // Update accessible label if icon changes
          const icon = this.attr(`icon`) ?? ``;
          if (!this.params.label && this.isIconOnly() && this.getAttribute(`aria-label`) !== icon) {
            this.setAttribute(`aria-label`, icon);
          }
        },
      },
    });
  }
  connectedCallback(): void {
    // Writing the observed `class` attribute before Panel initializes avoids queuing a second render.
    this.syncHostRootClass();
    this.syncHostDataAttributes();
    super.connectedCallback();
    // Add accessible label for icon-only buttons
    const icon = this.attr(`icon`);
    if (icon && !this.params.label && this.isIconOnly()) {
      this.setAttribute(`aria-label`, icon);
    }
    if (this.params.label) {
      this.setAttribute(`aria-label`, this.params.label);
    }
  }

  attributeChangedCallback(attr: string, oldValue: string | null, newValue: string | null): void {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `class`) {
      this.syncHostRootClass();
    } else if ((HOST_DATA_ATTR_SOURCES as readonly string[]).includes(attr)) {
      this.syncHostDataAttributes();
    }
  }

  /**
   * Callers own the `class` attribute outright (a Panel `attrs.class` and React's `className`
   * both rewrite it), so the root class is re-added whenever the attribute changes. The
   * `contains` guard matters: `classList.add` sets the attribute even when the token is already
   * present, which would re-enter this callback.
   */
  private syncHostRootClass(): void {
    if (!this.classList.contains(BUTTON_ROOT_CLASS)) {
      this.classList.add(BUTTON_ROOT_CLASS);
    }
  }

  private syncHostDataAttributes(): void {
    this.setHostAttribute(`data-button-theme`, this.attr(`theme`) ?? null);
    this.setHostAttribute(`data-button-size`, this.attr(`size`) ?? null);
    this.setHostAttribute(`data-button-layout`, this.attr(`layout`) ?? null);
    this.setHostAttribute(`data-button-icon`, this.attr(`icon`) || null);
    this.setHostAttribute(`data-button-pressed`, this.attr(`pressed`) ? `true` : null);
    this.setHostAttribute(`data-button-disabled`, this.attr(`disabled`) ? `true` : null);
    this.setHostAttribute(`data-button-readonly`, this.attr(`readonly`) ? `true` : null);
  }

  private setHostAttribute(name: string, value: string | null): void {
    if (value === null) {
      this.removeAttribute(name);
    } else if (this.getAttribute(name) !== value) {
      this.setAttribute(name, value);
    }
  }

  getContent() {
    return this.innerHTML.trim();
  }

  getTooltip() {
    // React callsites pass `tooltip` as a param; Panel JSX callsites pass it inside
    // `attrs={{…}}`, where `defineAndMakeJsxFactory` JSON-stringifies it into a DOM
    // attribute. Both forms have to be read here.
    const tooltip = this.params.tooltip?.text ? this.params.tooltip : this.attr(`tooltip`);
    if (tooltip?.text) {
      return tooltip;
    } else if (this.params.label) {
      return {text: this.params.label};
    }

    return null;
  }

  handleClick = (ev: MouseEvent) => {
    const href = this.attr(`href`);
    if (href && !this.attr(`download`)) {
      this.getContext(`navManager`).navigate(href, {
        triggerEvent: ev,
        fromAnchorLink: true,
        anchorTarget: this.attr(`target`),
      });
    }
  };

  isIconOnly() {
    const content = this.getContent();
    return (!content && !!this.attr(`icon`)) || content.length === 1;
  }

  // A click dispatched on the host never reaches the anchor's click handler, so `href` buttons
  // activate the anchor itself to navigate.
  protected getKeyboardActivationTarget(): HTMLElement {
    return this.el.querySelector<HTMLAnchorElement>(`a[href]`) ?? this;
  }
}

export const ButtonEl = defineAndMakeJsxFactory<ButtonJsxProps>(`mp-button`, Button);
